import { error, EventTarget } from 'cc';
import IMessageSource from './MessageSource/IMessageSource';
import { LobbyMessagePathName, LobbyMessage, ILobbyMessageDataType, LanguageData, PageStateData, FullscreenData, SoundData, PageName } from './Data/Lobby.dto';
import { String } from '../common/scripts/Toolkit/Utils/UtilityType';

// 通訊模組, 大廳與子遊戲通訊用
export default class LobbyCommunicator {
    private _event: EventTarget = new EventTarget();
    private _messageSourceLoadedPromise: Promise<void>;
    private _messageSourceLoadedResolve: () => void;
    private _messageSource: IMessageSource;

    constructor() {
        this._messageSourceLoadedPromise = new Promise((resolve) => {
            this._messageSourceLoadedResolve = resolve;
        });
    }

    public get waitForMessageSourceLoaded() {
        return this._messageSourceLoadedPromise;
    }

    public async send<TKey extends keyof ILobbyMessageDataType>(pathName: TKey, payload?: ILobbyMessageDataType[TKey]) {
        const data: LobbyMessage = { path: pathName as LobbyMessagePathName, data: payload };
        const stringifyData = JSON.stringify(data);
        await this.waitForMessageSourceLoaded;
        this._messageSource.sendMessage(stringifyData);
    }

    /**
     * 大廳同步遊戲語系
     */
    public async syncGameLanguage(language: string) {
        const data: LanguageData = { language };
        await this.send(LobbyMessagePathName.Language, data);
    }

    /**
     * 大廳同步遊戲音量與靜音狀態
     */
    public async syncGameSound(data: SoundData) {
        await this.send(LobbyMessagePathName.Sound, data);
    }

    /**
     * 大廳請求遊戲開啟特定頁面
     */
    public async requestGameOpenPage(page: PageName) {
        const data: PageStateData = { page };
        await this.send(LobbyMessagePathName.PageOpen, data);
    }

    /**
     * 大廳通知遊戲指定頁面已關閉
     */
    public async notifyGamePageClosed(page: PageName) {
        const data: PageStateData = { page };
        await this.send(LobbyMessagePathName.PageClosed, data);
    }

    /**
     * 大廳請求遊戲進入全螢幕
     */
    public async requestGameEnterFullscreen() {
        const data: FullscreenData = { enabled: true };
        await this.send(LobbyMessagePathName.Fullscreen, data);
    }

    /**
     * 大廳請求關閉遊戲
     */
    public async requestGameClose() {
        await this.send(LobbyMessagePathName.Close);
    }

    public on<TKey extends keyof ILobbyMessageDataType, TFunction extends (data: ILobbyMessageDataType[TKey], subject: string) => void>(
        pathName: String<TKey>,
        callback: TFunction,
        thisArg?: any,
        once?: boolean,
    ) {
        this._event.on(pathName, callback, thisArg, once);
    }

    public once<TKey extends keyof ILobbyMessageDataType, TFunction extends (data: ILobbyMessageDataType[TKey], subject: string) => void>(
        pathName: String<TKey>,
        callback: TFunction,
        thisArg?: any,
    ) {
        this._event.once(pathName, callback, thisArg);
    }

    public off<TKey extends keyof ILobbyMessageDataType, TFunction extends (data: ILobbyMessageDataType[TKey], subject: string) => void>(
        pathName: String<TKey>,
        callback: TFunction,
        thisArg?: any,
    ) {
        this._event.off(pathName, callback, thisArg);
    }

    public attachMessageSource(messageSource: IMessageSource) {
        this._messageSource = messageSource;
        this._messageSourceLoadedResolve();
    }

    public async initEvent() {
        await this.waitForMessageSourceLoaded;
        this._messageSource.setupMessageReceiver((messageData) => {
            try {
                const message = JSON.parse(messageData) as LobbyMessage;
                const { path, data } = message;
                // log('[Lobby Msg]', message);
                if (!path) {
                    return;
                }
                if (Object.values(LobbyMessagePathName).includes(path)) {
                    // data 需要自行給予型別並檢查是否 valid
                    this._event.emit(path, data);
                }
            } catch (ex) {
                error(ex);
            }
        });
    }
}
